import React from "react";
import useSeo from "@/hooks/useSeo";
import LegalLayout, { P, Strong, UL, ExtLink, InLink, DataTable, InfoGrid } from "@/components/LegalLayout";
import { COMPANY } from "@/data/company";

const UPDATED = "1 juni 2026";

const companyInfo = [
  { label: "Bedrijfsnaam", value: COMPANY.name },
  { label: "Eigenaar", value: COMPANY.owner },
  { label: "Vestigingsplaats", value: COMPANY.location },
  ...(COMPANY.kvk ? [{ label: "KVK-nummer", value: COMPANY.kvk }] : []),
  { label: "E-mail", value: <a href={`mailto:${COMPANY.email}`} className="text-electric hover:underline underline-offset-4">{COMPANY.email}</a> },
  { label: "Telefoon / WhatsApp", value: COMPANY.phoneDisplay },
];

const sections = [
  {
    id: "verantwoordelijke",
    title: "Wie is verantwoordelijk voor jouw gegevens?",
    content: (
      <>
        <P>
          {COMPANY.name}, gevestigd in {COMPANY.location.split(",")[0]}, is de <Strong>verwerkingsverantwoordelijke</Strong> voor
          de verwerking van persoonsgegevens zoals beschreven in dit privacybeleid, in de zin van de Algemene
          Verordening Gegevensbescherming (AVG / GDPR).
        </P>
        <InfoGrid items={companyInfo} />
      </>
    ),
  },
  {
    id: "gegevens",
    title: "Welke persoonsgegevens verwerken wij?",
    content: (
      <>
        <P>Wij verwerken alleen gegevens die jij zelf aan ons verstrekt of die nodig zijn om de website veilig te laten werken:</P>
        <UL
          items={[
            <><Strong>Contactformulier & e-mail:</Strong> naam, e-mailadres, telefoonnummer (optioneel), bedrijfsnaam (optioneel), de dienst waarin je interesse hebt en je bericht.</>,
            <><Strong>AI Growth Scan:</Strong> jouw antwoorden over je onderneming (zoals branche, doelen en huidige marketingaanpak) en — alleen als je het rapport wilt ontvangen — je naam en e-mailadres.</>,
            <><Strong>WhatsApp & telefoon:</Strong> als je ons via WhatsApp benadert, verwerken wij je naam, telefoonnummer en berichtinhoud via de dienst van Meta (WhatsApp).</>,
            <><Strong>Automatisch verzamelde gegevens:</Strong> IP-adres (kortdurend, uitsluitend voor beveiliging en misbruikpreventie), browsertype en jouw cookievoorkeuren.</>,
          ]}
        />
        <P>
          Wij verzamelen <Strong>geen bijzondere categorieën persoonsgegevens</Strong> (zoals gezondheids- of geloofsgegevens) en
          onze diensten zijn niet gericht op personen jonger dan 16 jaar.
        </P>
      </>
    ),
  },
  {
    id: "doeleinden",
    title: "Waarvoor en op welke grondslag?",
    content: (
      <>
        <P>Wij verwerken persoonsgegevens uitsluitend voor onderstaande doelen, telkens op basis van een geldige AVG-grondslag (artikel 6 AVG):</P>
        <DataTable
          testid="privacy-purposes-table"
          head={["Doel", "Gegevens", "Grondslag"]}
          rows={[
            ["Beantwoorden van je aanvraag of offerte", "Contactgegevens + bericht", "Uitvoering (precontractuele) overeenkomst — art. 6 lid 1 sub b"],
            ["Uitvoeren AI Growth Scan + toesturen rapport", "Scanantwoorden, naam, e-mail", "Uitvoering van de door jou gevraagde dienst — sub b"],
            ["Dienstverlening & facturatie aan klanten", "Contact- en factuurgegevens", "Overeenkomst + wettelijke plicht — sub b en c"],
            ["Websitestatistieken (Google Analytics 4)", "Pseudonieme gebruiksdata", "Toestemming — sub a"],
            ["Marketing & advertenties (Meta Pixel, LinkedIn)", "Cookie- en advertentiedata", "Toestemming — sub a"],
            ["Beveiliging & misbruikpreventie", "IP-adres, technische logs", "Gerechtvaardigd belang — sub f"],
          ]}
        />
      </>
    ),
  },
  {
    id: "ontvangers",
    title: "Met wie delen wij gegevens?",
    content: (
      <>
        <P>
          Wij <Strong>verkopen jouw gegevens nooit</Strong>. We delen ze alleen met dienstverleners (verwerkers) die
          ons helpen de website en onze diensten te laten draaien, en waar vereist onder een verwerkersovereenkomst:
        </P>
        <UL
          items={[
            <><Strong>Resend</Strong> — verzending van e-mailnotificaties en scanrapporten.</>,
            <><Strong>Hosting- en databaseproviders</Strong> — beveiligde opslag van formulierinzendingen.</>,
            <><Strong>Anthropic</Strong> — AI-analyse van uitsluitend de door jou ingevulde AI Growth Scan-antwoorden.</>,
            <><Strong>Google (Analytics), Meta en LinkedIn</Strong> — uitsluitend nadat jij daarvoor via de cookiebanner toestemming hebt gegeven.</>,
            <><Strong>Meta (WhatsApp)</Strong> — wanneer jij zelf via WhatsApp contact met ons opneemt.</>,
          ]}
        />
        <P>
          Sommige van deze partijen zijn (deels) gevestigd buiten de Europese Economische Ruimte, waaronder de
          Verenigde Staten. In die gevallen zorgen wij voor passende waarborgen, zoals de{" "}
          <Strong>EU-standaardcontractbepalingen (SCC's)</Strong> en/of certificering onder het{" "}
          <Strong>EU-VS Data Privacy Framework</Strong>.
        </P>
      </>
    ),
  },
  {
    id: "bewaartermijnen",
    title: "Hoe lang bewaren wij gegevens?",
    content: (
      <>
        <P>Wij bewaren gegevens niet langer dan nodig voor het doel waarvoor ze zijn verzameld:</P>
        <DataTable
          testid="privacy-retention-table"
          head={["Gegevens", "Bewaartermijn"]}
          rows={[
            ["Contactaanvragen zonder vervolg", "Maximaal 12 maanden na het laatste contact"],
            ["AI Growth Scan-resultaten", "Maximaal 12 maanden"],
            ["Klantgegevens & administratie", "7 jaar (wettelijke fiscale bewaarplicht)"],
            ["Cookievoorkeuren", "180 dagen — daarna vragen we opnieuw om toestemming"],
          ]}
        />
      </>
    ),
  },
  {
    id: "rechten",
    title: "Jouw rechten onder de AVG",
    content: (
      <>
        <P>Je hebt als betrokkene de volgende rechten:</P>
        <UL
          items={[
            <><Strong>Inzage</Strong> — opvragen welke gegevens wij van je hebben.</>,
            <><Strong>Rectificatie</Strong> — onjuiste gegevens laten corrigeren.</>,
            <><Strong>Verwijdering</Strong> — je gegevens laten wissen ("recht om vergeten te worden").</>,
            <><Strong>Beperking</Strong> — de verwerking tijdelijk laten beperken.</>,
            <><Strong>Bezwaar</Strong> — bezwaar maken tegen verwerking op basis van gerechtvaardigd belang.</>,
            <><Strong>Dataportabiliteit</Strong> — je gegevens in een gangbaar formaat ontvangen.</>,
            <><Strong>Toestemming intrekken</Strong> — op elk moment, zonder opgaaf van reden (bijvoorbeeld via de cookie-instellingen).</>,
          ]}
        />
        <P>
          Stuur je verzoek naar <ExtLink href={`mailto:${COMPANY.email}`}>{COMPANY.email}</ExtLink>. We reageren binnen{" "}
          <Strong>30 dagen</Strong> en kunnen je vragen om je identiteit te bevestigen voordat we gegevens verstrekken of wijzigen.
        </P>
        <P>
          Niet tevreden over hoe wij met je gegevens omgaan? Je hebt altijd het recht een klacht in te dienen bij de{" "}
          <ExtLink href="https://www.autoriteitpersoonsgegevens.nl">Autoriteit Persoonsgegevens</ExtLink>.
        </P>
      </>
    ),
  },
  {
    id: "beveiliging",
    title: "Hoe beveiligen wij jouw gegevens?",
    content: (
      <>
        <P>Wij nemen passende technische en organisatorische maatregelen om jouw gegevens te beschermen:</P>
        <UL
          items={[
            "Versleutelde verbindingen (TLS/SSL) op de hele website.",
            "Toegang tot persoonsgegevens is strikt beperkt en afgeschermd met authenticatie.",
            "Rate limiting en misbruikdetectie op alle formulieren.",
            "Dataminimalisatie: we vragen alleen wat we echt nodig hebben.",
          ]}
        />
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies",
    content: (
      <P>
        Wij gebruiken cookies en vergelijkbare technieken. Welke dat precies zijn, waarom we ze gebruiken en hoe je jouw
        voorkeuren beheert, lees je in ons <InLink to="/cookies">cookiebeleid</InLink>. Je kunt je keuze op elk moment
        aanpassen via de link "Cookie-instellingen" onderaan elke pagina.
      </P>
    ),
  },
  {
    id: "wijzigingen",
    title: "Wijzigingen & contact",
    content: (
      <>
        <P>
          Wij kunnen dit privacybeleid aanpassen, bijvoorbeeld bij nieuwe diensten of gewijzigde wetgeving. De meest
          actuele versie staat altijd op deze pagina; bovenaan zie je de datum van de laatste update.
        </P>
        <P>
          Vragen? Mail naar <ExtLink href={`mailto:${COMPANY.email}`}>{COMPANY.email}</ExtLink> of stuur een WhatsApp-bericht
          naar {COMPANY.phoneDisplay}.
        </P>
      </>
    ),
  },
];

const PrivacyPage = () => {
  useSeo({
    title: "Privacybeleid | UPPIT",
    description:
      "Lees hoe UPPIT omgaat met jouw persoonsgegevens: welke gegevens we verwerken, waarom, hoe lang we ze bewaren en welke rechten jij hebt onder de AVG.",
    path: "/privacy",
    ogSlug: "page/privacy",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Privacybeleid UPPIT",
      url: "https://uppit.nl/privacy",
      inLanguage: "nl-NL",
      dateModified: "2026-06-01",
    },
  });

  return (
    <LegalLayout
      testid="privacy-page"
      crumbLabel="Privacybeleid"
      crumbHref="/privacy"
      kicker="Juridisch · AVG"
      italic="Privacybeleid."
      bold="Transparant over jouw gegevens."
      lede="Wij gaan zorgvuldig en transparant om met persoonsgegevens. Hieronder lees je precies wat we verzamelen, waarom, hoe lang we het bewaren en welke rechten jij hebt."
      updated={UPDATED}
      sections={sections}
    />
  );
};

export default PrivacyPage;
